import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { API } from '../utils/apiConfig';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  School, Plus, CalendarDays, ListChecks, Search, Edit2, Trash2, Loader2,
  Download, Upload, Users, Sparkles, X, Send,
} from 'lucide-react';

const PRESETS = [
  { value: 'all', label: 'All Upcoming & Past' },
  { value: 'this_month', label: 'This Month' },
  { value: 'next_month', label: 'Next Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_year', label: 'This Year' },
  { value: 'month', label: 'Pick a Month' },
  { value: 'custom', label: 'Custom Date Range' },
];

const emptyForm = { date: '', end_date: '', as_range: false, vacation: '', no_of_holidays: '1', notes: '', maks_tasks: '' };
const emptyRecipient = { name: '', mobile: '', is_active: true };

const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  const day = d.getDate();
  const month = d.toLocaleString('en-US', { month: 'short' });
  return `${day}-${month}`;
};

const DAY_COLORS = {
  Saturday: 'bg-indigo-100 text-indigo-700', Sunday: 'bg-rose-100 text-rose-700',
};

export default function SchoolPage() {
  const { admin } = useAuth();
  const isAdmin = admin?.role === 'admin' || admin?.email === 'admin@maks.com';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ holidays: [], stats: { total_records: 0, total_holiday_days: 0 } });
  const [filters, setFilters] = useState({ preset: 'this_month', month: '', date_from: '', date_to: '', search: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [recipientForm, setRecipientForm] = useState(emptyRecipient);
  const [recipientEditing, setRecipientEditing] = useState(null);
  const [testSending, setTestSending] = useState(false);

  const [templateConfig, setTemplateConfig] = useState(null);
  const [templateForm, setTemplateForm] = useState({ name: '', body: '' });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [schoolSettings, setSchoolSettings] = useState(null);
  const [triggerTime, setTriggerTime] = useState('09:00');
  const [overrideOpenTime, setOverrideOpenTime] = useState('06:00');
  const [overrideCloseTime, setOverrideCloseTime] = useState('23:30');
  const [savingTriggerTime, setSavingTriggerTime] = useState(false);

  const buildParams = useCallback(() => {
    const params = {};
    if (filters.search) params.search = filters.search;
    if (filters.preset === 'month' && filters.month) params.month = filters.month;
    else if (filters.preset === 'custom') {
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
    } else if (['this_month', 'next_month', 'last_month', 'this_year'].includes(filters.preset)) {
      params.preset = filters.preset;
    }
    return params;
  }, [filters]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/school/holidays`, { params: buildParams() });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load school holidays');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const fetchRecipients = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/school/recipients`);
      setRecipients(res.data.recipients || []);
    } catch (e) { /* silent */ }
  }, []);

  const fetchTemplateAndSettings = useCallback(async () => {
    try {
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API}/school/template-config`),
        axios.get(`${API}/school/settings`),
      ]);
      setTemplateConfig(tRes.data);
      setTemplateForm({ name: tRes.data.name || '', body: tRes.data.body || '' });
      setSchoolSettings(sRes.data);
      setTriggerTime(sRes.data.trigger_time || '09:00');
      setOverrideOpenTime(sRes.data.override_open_time || '06:00');
      setOverrideCloseTime(sRes.data.override_close_time || '23:30');
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchRecipients(); }, [fetchRecipients]);
  useEffect(() => { if (recipientsOpen) fetchTemplateAndSettings(); }, [recipientsOpen, fetchTemplateAndSettings]);

  const saveTemplateConfig = async () => {
    if (!templateForm.name.trim()) { toast.error('Template name is required'); return; }
    setSavingTemplate(true);
    try {
      await axios.put(`${API}/school/template-config`, {
        name: templateForm.name.trim(), body: templateForm.body, params: templateConfig?.params,
      });
      toast.success('Template updated');
      fetchTemplateAndSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const saveTriggerTime = async () => {
    setSavingTriggerTime(true);
    try {
      await axios.put(`${API}/school/settings`, {
        trigger_time: triggerTime,
        override_open_time: overrideOpenTime,
        override_close_time: overrideCloseTime,
      });
      toast.success(`Settings saved`);
      fetchTemplateAndSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update settings');
    } finally {
      setSavingTriggerTime(false);
    }
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({ date: row.date, end_date: '', as_range: false, vacation: row.vacation || '', no_of_holidays: String(row.no_of_holidays ?? 1), notes: row.notes || '', maks_tasks: row.maks_tasks || '' });
    setDialogOpen(true);
  };
  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.date) { toast.error('Date is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/school/holidays/${editing.id}`, {
          date: form.date, vacation: form.vacation, no_of_holidays: parseFloat(form.no_of_holidays) || 0,
          notes: form.notes, maks_tasks: form.maks_tasks,
        });
        toast.success('Holiday updated');
      } else {
        const payload = {
          date: form.date, vacation: form.vacation, no_of_holidays: parseFloat(form.no_of_holidays) || 1,
          notes: form.notes, maks_tasks: form.maks_tasks,
        };
        if (form.as_range && form.end_date) payload.end_date = form.end_date;
        const res = await axios.post(`${API}/school/holidays`, payload);
        const { created, skipped } = res.data;
        toast.success(`Added ${created.length} holiday record(s)${skipped.length ? ` — skipped ${skipped.length} (already exist)` : ''}`);
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save holiday');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/school/holidays/${deleting.id}`);
      toast.success('Holiday deleted');
      setDeleting(null);
      fetchData();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/school/holidays/export`, { params: buildParams(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'school_holidays.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Exported to Excel');
    } catch (e) {
      toast.error('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/school/holidays/import`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { imported, skipped, invalid } = res.data;
      toast.success(`Imported ${imported} · Skipped ${skipped} (duplicates) · Invalid ${invalid}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to import file');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ---- recipients ----
  const openRecipientCreate = () => { setRecipientEditing(null); setRecipientForm(emptyRecipient); };
  const openRecipientEdit = (r) => { setRecipientEditing(r); setRecipientForm({ name: r.name, mobile: r.mobile, is_active: r.is_active }); };

  const saveRecipient = async () => {
    if (!recipientForm.name.trim() || !recipientForm.mobile.trim()) { toast.error('Name and mobile are required'); return; }
    try {
      if (recipientEditing) {
        await axios.put(`${API}/school/recipients/${recipientEditing.id}`, recipientForm);
        toast.success('Recipient updated');
      } else {
        await axios.post(`${API}/school/recipients`, recipientForm);
        toast.success('Recipient added');
      }
      setRecipientEditing(null);
      setRecipientForm(emptyRecipient);
      fetchRecipients();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save recipient');
    }
  };

  const deleteRecipient = async (r) => {
    try {
      await axios.delete(`${API}/school/recipients/${r.id}`);
      toast.success('Recipient removed');
      fetchRecipients();
    } catch (e) {
      toast.error('Failed to delete recipient');
    }
  };

  const toggleRecipientActive = async (r) => {
    try {
      await axios.put(`${API}/school/recipients/${r.id}`, { name: r.name, mobile: r.mobile, is_active: !r.is_active });
      fetchRecipients();
    } catch (e) {
      toast.error('Failed to update recipient');
    }
  };

  const sendTestReminder = async () => {
    setTestSending(true);
    try {
      const res = await axios.post(`${API}/school/holidays/test-reminder`);
      const sentCount = res.data.results.filter((r) => r.sent).length;
      if (sentCount === 0) {
        toast.error(`Sent to 0/${res.data.results.length} — check the template config below, the WABA provider likely rejected the payload`);
      } else {
        toast.success(`Sent to ${sentCount}/${res.data.results.length} recipient(s)`);
      }
      fetchTemplateAndSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test reminder');
    } finally {
      setTestSending(false);
    }
  };

  const rows = data.holidays || [];
  const stats = data.stats || {};

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="school-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-100">
              <School className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">School</h1>
              <p className="text-sm text-slate-500">School Holidays register</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" className="gap-2" onClick={() => setRecipientsOpen(true)} data-testid="recipients-btn">
              <Users className="w-4 h-4" /> <span className="hidden sm:inline">Recipients</span>
            </Button>
            <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleImportFile} data-testid="import-file-input" />
            <Button variant="outline" className="gap-2" disabled={importing} onClick={() => fileInputRef.current?.click()} data-testid="import-holidays-btn">
              {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              <span className="hidden sm:inline">Import</span>
            </Button>
            <Button variant="outline" className="gap-2" disabled={exporting} onClick={handleExport} data-testid="export-holidays-btn">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              <span className="hidden sm:inline">Export</span>
            </Button>
            <Button onClick={openCreate} className="gap-2" data-testid="add-holiday-btn">
              <Plus className="w-4 h-4" /> Add Holiday
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <Card className="p-4 border-slate-200" data-testid="stat-records">
            <div className="flex items-center gap-2 text-indigo-600"><ListChecks className="w-4 h-4" /><span className="text-xs font-medium">Holiday Records</span></div>
            <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total_records || 0}</p>
          </Card>
          <Card className="p-4 border-slate-200" data-testid="stat-days">
            <div className="flex items-center gap-2 text-amber-600"><CalendarDays className="w-4 h-4" /><span className="text-xs font-medium">Total Holiday Days</span></div>
            <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total_holiday_days || 0}</p>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-3 border-slate-200">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input placeholder="Search vacation / notes" value={filters.search}
                onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                className="pl-9" data-testid="holiday-search" />
            </div>
            <Select value={filters.preset} onValueChange={(v) => setFilters((p) => ({ ...p, preset: v }))}>
              <SelectTrigger className="w-44" data-testid="filter-preset"><SelectValue /></SelectTrigger>
              <SelectContent>{PRESETS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
            {filters.preset === 'month' && (
              <Input type="month" value={filters.month} onChange={(e) => setFilters((p) => ({ ...p, month: e.target.value }))} className="w-40" data-testid="filter-month" />
            )}
            {filters.preset === 'custom' && (
              <>
                <Input type="date" value={filters.date_from} onChange={(e) => setFilters((p) => ({ ...p, date_from: e.target.value }))} className="w-36" data-testid="filter-date-from" />
                <span className="text-slate-400 text-sm">to</span>
                <Input type="date" value={filters.date_to} onChange={(e) => setFilters((p) => ({ ...p, date_to: e.target.value }))} className="w-36" data-testid="filter-date-to" />
              </>
            )}
          </div>
        </Card>

        {/* List */}
        {loading ? (
          <Card className="p-12 text-center border-slate-200"><Loader2 className="w-8 h-8 mx-auto text-amber-500 animate-spin" /></Card>
        ) : rows.length === 0 ? (
          <Card className="p-12 text-center border-slate-200">
            <School className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            <p className="text-slate-600">No holiday records found</p>
            <p className="text-sm text-slate-400">Click "Add Holiday" to add the first one</p>
          </Card>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="space-y-3 sm:hidden">
              {rows.map((r) => (
                <Card key={r.id} className="p-4 border-slate-200" data-testid={`holiday-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-900">{formatDateShort(r.date)}</p>
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${DAY_COLORS[r.day] || 'bg-slate-100 text-slate-600'}`}>{r.day}</span>
                    </div>
                    {r.is_auto_generated && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-700 flex items-center gap-1" data-testid={`auto-badge-m-${r.id}`}><Sparkles className="w-3 h-3" />Auto</span>}
                  </div>
                  <p className="text-sm text-slate-700 mt-2 font-medium">{r.vacation || '—'}</p>
                  <div className="mt-1 text-xs text-slate-500 space-y-0.5">
                    <p>Holidays: <b>{r.no_of_holidays}</b></p>
                    {r.notes && <p>Notes: {r.notes}</p>}
                    {r.maks_tasks && <p>MAKS Tasks: {r.maks_tasks}</p>}
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => openEdit(r)} data-testid={`edit-holiday-m-${r.id}`}><Edit2 className="w-4 h-4 mr-1" />Edit</Button>
                    {isAdmin && (
                      <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => setDeleting(r)} data-testid={`delete-holiday-m-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>

            {/* Desktop table */}
            <Card className="border-slate-200 overflow-hidden hidden sm:block">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="holidays-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      {['Date', 'Day', 'Vacation', 'No. of Holidays', 'Notes', 'MAKS Tasks', 'Actions'].map((h) => (
                        <th key={h} className="text-left p-3 font-semibold text-slate-700 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`holiday-row-${r.id}`}>
                        <td className="p-3 font-medium text-slate-900 whitespace-nowrap">{formatDateShort(r.date)}</td>
                        <td className="p-3 whitespace-nowrap">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${DAY_COLORS[r.day] || 'bg-slate-100 text-slate-600'}`}>{r.day}</span>
                        </td>
                        <td className="p-3 text-slate-700">
                          <div className="flex items-center gap-2">
                            {r.vacation || '—'}
                            {r.is_auto_generated && <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-sky-100 text-sky-700 flex items-center gap-1 shrink-0" data-testid={`auto-badge-${r.id}`}><Sparkles className="w-3 h-3" />Auto</span>}
                          </div>
                        </td>
                        <td className="p-3 font-semibold text-slate-700">{r.no_of_holidays}</td>
                        <td className="p-3 text-slate-600 text-xs max-w-[180px] truncate" title={r.notes}>{r.notes || '—'}</td>
                        <td className="p-3 text-slate-600 text-xs max-w-[180px] truncate" title={r.maks_tasks}>{r.maks_tasks || '—'}</td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-holiday-${r.id}`}><Edit2 className="w-4 h-4" /></Button>
                            {isAdmin && (
                              <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => setDeleting(r)} data-testid={`delete-holiday-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Add / Edit Holiday Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg w-[95vw]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><CalendarDays className="w-5 h-5 text-amber-600" />{editing ? 'Edit Holiday' : 'Add Holiday'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Date <span className="text-red-500">*</span></Label>
                <Input type="date" value={form.date} onChange={(e) => setField('date', e.target.value)} data-testid="form-date" />
              </div>
              {!editing && (
                <div className="space-y-1.5">
                  <Label className="flex items-center gap-2">
                    <input type="checkbox" checked={form.as_range} onChange={(e) => setField('as_range', e.target.checked)} data-testid="form-as-range" />
                    Multi-day range
                  </Label>
                  {form.as_range && (
                    <Input type="date" value={form.end_date} onChange={(e) => setField('end_date', e.target.value)} placeholder="End date" data-testid="form-end-date" />
                  )}
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Vacation</Label>
              <Input value={form.vacation} onChange={(e) => setField('vacation', e.target.value)} placeholder="e.g. Ganesh Chaturthi Vacation" data-testid="form-vacation" />
            </div>
            <div className="space-y-1.5">
              <Label>No. of Holidays</Label>
              <Input type="number" min="0" step="0.5" value={form.no_of_holidays} onChange={(e) => setField('no_of_holidays', e.target.value)} data-testid="form-no-of-holidays" />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setField('notes', e.target.value)} data-testid="form-notes" />
            </div>
            <div className="space-y-1.5">
              <Label>MAKS Tasks</Label>
              <Textarea rows={2} value={form.maks_tasks} onChange={(e) => setField('maks_tasks', e.target.value)} data-testid="form-maks-tasks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-holiday-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : (editing ? 'Update' : 'Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete holiday?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">This will permanently delete the holiday record for <b>{formatDateShort(deleting?.date)}</b>{deleting?.is_auto_generated ? ' (auto-generated Saturday)' : ''}.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDelete} data-testid="confirm-delete-holiday-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Recipients Dialog */}
      <Dialog open={recipientsOpen} onOpenChange={setRecipientsOpen}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[85vh] overflow-y-auto" data-testid="recipients-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-amber-600" />Notification Recipients</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">These staff members receive WhatsApp reminders 4, 2 &amp; 1 day before the next school holiday.</p>
          <div className="space-y-2">
            {recipients.map((r) => (
              <div key={r.id} className="flex items-center gap-2 p-2.5 border rounded-lg" data-testid={`recipient-row-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-900 truncate">{r.name}</p>
                  <p className="text-xs text-slate-500">{r.mobile}</p>
                </div>
                <Switch checked={r.is_active} onCheckedChange={() => toggleRecipientActive(r)} data-testid={`recipient-active-${r.id}`} />
                <Button variant="ghost" size="sm" onClick={() => openRecipientEdit(r)} data-testid={`recipient-edit-${r.id}`}><Edit2 className="w-4 h-4" /></Button>
                {isAdmin && (
                  <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => deleteRecipient(r)} data-testid={`recipient-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                )}
              </div>
            ))}
            {recipients.length === 0 && <p className="text-sm text-slate-400 text-center py-3">No recipients configured yet</p>}
          </div>
          <div className="border-t pt-3 space-y-2">
            <p className="text-sm font-medium text-slate-700">{recipientEditing ? 'Edit recipient' : 'Add recipient'}</p>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Name" value={recipientForm.name} onChange={(e) => setRecipientForm((p) => ({ ...p, name: e.target.value }))} data-testid="recipient-form-name" />
              <Input placeholder="Mobile" value={recipientForm.mobile} onChange={(e) => setRecipientForm((p) => ({ ...p, mobile: e.target.value }))} data-testid="recipient-form-mobile" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={saveRecipient} data-testid="recipient-save-btn">{recipientEditing ? 'Update' : 'Add'}</Button>
              {recipientEditing && <Button size="sm" variant="ghost" onClick={openRecipientCreate}><X className="w-4 h-4" /></Button>}
            </div>
          </div>

          {/* Last sent status */}
          <div className="border-t pt-3 space-y-1" data-testid="last-sent-status">
            <p className="text-sm font-medium text-slate-700">Last Reminder Sent</p>
            {schoolSettings?.last_sent_at ? (
              <p className="text-xs text-slate-500">
                <b>{new Date(schoolSettings.last_sent_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</b>
                {' '}— to {schoolSettings.last_sent_recipient_count} recipient(s), for holiday: {schoolSettings.last_sent_next_holiday}
              </p>
            ) : (
              <p className="text-xs text-slate-400">No reminder has been sent yet</p>
            )}
          </div>

          {/* Trigger time */}
          <div className="border-t pt-3 space-y-2">
            <p className="text-sm font-medium text-slate-700">Notification Trigger Time</p>
            <p className="text-xs text-slate-500">Reminders (4/2/1 days before) will send once this IST time is reached each day.</p>
            <div className="flex gap-2">
              <Input type="time" value={triggerTime} onChange={(e) => setTriggerTime(e.target.value)} className="w-32" data-testid="trigger-time-input" />
              <Button size="sm" variant="outline" onClick={saveTriggerTime} disabled={savingTriggerTime} data-testid="save-trigger-time-btn">
                {savingTriggerTime ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>

          {/* Court Override Hours */}
          <div className="border-t pt-3 space-y-2" data-testid="override-hours-section">
            <p className="text-sm font-medium text-slate-700">Court Override Hours (Holiday)</p>
            <p className="text-xs text-slate-500">
              MAKS Sports Complex courts will be automatically set to these hours on school holiday dates (applied ~7 days in advance).
            </p>
            <div className="flex gap-3 items-end">
              <div className="space-y-1">
                <Label className="text-xs">Open Time</Label>
                <Input type="time" value={overrideOpenTime} onChange={(e) => setOverrideOpenTime(e.target.value)} className="w-32" data-testid="override-open-time-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Close Time</Label>
                <Input type="time" value={overrideCloseTime} onChange={(e) => setOverrideCloseTime(e.target.value)} className="w-32" data-testid="override-close-time-input" />
              </div>
              <Button size="sm" variant="outline" onClick={saveTriggerTime} disabled={savingTriggerTime} data-testid="save-override-hours-btn">
                {savingTriggerTime ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>

          {/* Template config */}
          <div className="border-t pt-3 space-y-2" data-testid="template-config-section">
            <p className="text-sm font-medium text-slate-700">WABA Template</p>
            <p className="text-xs text-slate-500">Match this to your Meta-approved template exactly (name + placeholder count) to avoid the "parameters don't match" error.</p>
            <div className="space-y-1.5">
              <Label className="text-xs">Template Name</Label>
              <Input value={templateForm.name} onChange={(e) => setTemplateForm((p) => ({ ...p, name: e.target.value }))} data-testid="template-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Message Body (use {'{{1}}'}=Name, {'{{2}}'}=Next Holiday, {'{{3}}'}=Next 15 Days)</Label>
              <Textarea rows={3} value={templateForm.body} onChange={(e) => setTemplateForm((p) => ({ ...p, body: e.target.value }))} data-testid="template-body-input" />
            </div>
            {templateConfig?.preview && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-xs text-emerald-800 whitespace-pre-wrap" data-testid="template-preview">
                <b>Preview:</b> {templateConfig.preview}
              </div>
            )}
            <Button size="sm" onClick={saveTemplateConfig} disabled={savingTemplate} data-testid="save-template-btn">
              {savingTemplate ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Template'}
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" className="gap-2" disabled={testSending} onClick={sendTestReminder} data-testid="send-test-reminder-btn">
              {testSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send Test Reminder Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
